import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import WorkRecordApi from "../../api/workRecordApi";
import WorkRecordStatCard from "../../components/workrecord/WorkRecordStatCard";

import {
  ArrowLeft,
  Flame,
  Gauge,
  HeartPulse,
  Timer,
  Activity,
  Route,
  TrendingUp,
  ChevronDown,
  Calendar,
  Layers,
} from "lucide-react";

import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import { useJWT } from "../../contexts/JWTContext";

/* 날짜 변환 함수들 (기존 로직 유지) */
function getWeekRange(weekKey) {
  const [year, week] = weekKey.split("-").map(Number);
  const simple = new Date(year, 0, 1 + (week - 1) * 7);
  const dow = simple.getDay();
  let start = new Date(simple);
  if (dow <= 4) start.setDate(simple.getDate() - simple.getDay() + 1);
  else start.setDate(simple.getDate() + (8 - simple.getDay()));
  let end = new Date(start);
  end.setDate(start.getDate() + 6);
  return {
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
  };
}

function getMonthRange(monthKey) {
  const [year, month] = monthKey.split("-").map(Number);
  const startDate = `${year}-${String(month).padStart(2, "0")}-01`;
  const lastDay = new Date(year, month, 0).getDate();
  return {
    startDate,
    endDate: `${year}-${String(month).padStart(2, "0")}-${lastDay}`,
  };
}

function getYearRange(yearKey) {
  return { startDate: `${yearKey}-01-01`, endDate: `${yearKey}-12-31` };
}

function buildDailyDistanceChart(list, startDate, endDate) {
  const map = {};

  // 1. 날짜별 합산
  list.forEach((item) => {
    const date = item.workRecordDate; // "2025-11-21"
    map[date] = (map[date] || 0) + Number(item.distance || 0);
  });

  // 2. 날짜 구간 채우기 (운동 안 한 날 = 0)
  const result = [];
  let cur = new Date(startDate);

  while (cur <= new Date(endDate)) {
    const key = cur.toISOString().slice(0, 10);
    result.push({
      name: key.slice(5), // MM-DD
      distance: map[key] || 0,
    });
    cur.setDate(cur.getDate() + 1);
  }

  return result;
}

function buildEmptyChart(startDate, endDate) {
  const result = [];
  let cur = new Date(startDate);
  while (cur <= new Date(endDate)) {
    const key = cur.toISOString().slice(0, 10);
    result.push({
      name: key.slice(5),
      distance: 0.2, // ⭐ 0이 아닌 값을 주어야 선이 위로 올라옵니다 (살짝 띄움)
      _isFake: true, // 가짜 데이터임을 표시
    });
    cur.setDate(cur.getDate() + 1);
  }
  return result;
}

function StatsWorkRecordPage() {
  const navigate = useNavigate();
  const { userId, isLoading } = useJWT();
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [period, setPeriod] = useState("");
  const [type, setType] = useState("");
  const [battleType, setBattleType] = useState("");
  const [weeks, setWeeks] = useState([]);
  const [months, setMonths] = useState([]);
  const [years, setYears] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);

  const isAllZero =
    chartData.length > 0 && chartData.every((d) => d._isFake === true);

  const mainTeal = "rgb(0, 224, 198)";
  const maxDistance = Math.max(...chartData.map((d) => d.distance || 0), 1);

  useEffect(() => {
    if (isLoading || !userId) return;
    WorkRecordApi.periods(userId)
      .then((res) => {
        setWeeks(res.data.weeks || []);
        setMonths(res.data.months || []);
        setYears(res.data.years || []);
      })
      .catch((err) => console.error("periods API error:", err));
  }, [isLoading, userId]);

  const formatValue = (value) => {
    if (value === null || value === undefined || value === "") return "-";

    const num = Number(value);
    if (Number.isNaN(num)) return "-";

    if (num === 0) return "-";

    // 소수점 2자리 반올림 → 불필요한 0 제거
    return num.toFixed(2).replace(/\.?0+$/, "");
  };

  const loadStats = useCallback(() => {
    if (!userId) return;
    let startDate = "";
    let endDate = "";
    if (period === "WEEK" && selectedWeek) {
      const r = getWeekRange(selectedWeek);
      startDate = r.startDate;
      endDate = r.endDate;
    } else if (period === "MONTH" && selectedMonth) {
      const r = getMonthRange(selectedMonth);
      startDate = r.startDate;
      endDate = r.endDate;
    } else if (period === "YEAR" && selectedYear) {
      const r = getYearRange(selectedYear);
      startDate = r.startDate;
      endDate = r.endDate;
    }
    setStatsLoading(true);
    WorkRecordApi.stats(userId, startDate, endDate, type, battleType)
      .then((res) => setStats(res.data?.[0] || null))
      .catch((err) => console.error("stats API error:", err))
      .finally(() => setStatsLoading(false));
  }, [
    userId,
    period,
    selectedWeek,
    selectedMonth,
    selectedYear,
    type,
    battleType,
  ]);

  const loadTrend = useCallback(() => {
    if (!userId) return;

    let startDate = "";
    let endDate = "";

    if (period === "WEEK" && selectedWeek) {
      ({ startDate, endDate } = getWeekRange(selectedWeek));
    } else if (period === "MONTH" && selectedMonth) {
      ({ startDate, endDate } = getMonthRange(selectedMonth));
    } else if (period === "YEAR" && selectedYear) {
      ({ startDate, endDate } = getYearRange(selectedYear));
    } else {
      // 기본: 최근 7일 빈 차트
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 6);

      const startDate = start.toISOString().slice(0, 10);
      const endDate = end.toISOString().slice(0, 10);

      setChartData(buildEmptyChart(startDate, endDate));
      return;
    }

    // 1단계: 먼저 "1로 채운" 빈 차트 표시
    setChartData(buildEmptyChart(startDate, endDate));

    // 2단계: 실제 데이터 조회
    WorkRecordApi.listByUser(userId, startDate, endDate, type)
      .then((res) => {
        if (!res.data || res.data.length === 0) {
          // 진짜 데이터가 없으면 1짜리 차트 그대로 유지
          return;
        }

        const chart = buildDailyDistanceChart(res.data, startDate, endDate);

        setChartData(chart);
      })
      .catch((err) => console.error("trend(listByUser) error:", err));
  }, [userId, period, selectedWeek, selectedMonth, selectedYear, type]);

  useEffect(() => {
    loadStats();
    loadTrend();
  }, [loadStats, loadTrend]);

  if (statsLoading) {
    return (
      <div className="h-screen flex justify-center items-center bg-white">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-[rgb(0,224,198)] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="h-screen overflow-hidden bg-gray-50 flex flex-col font-sans">
      {/* HEADER (상단 고정 유지) */}
      <header className="bg-white px-5 py-4 border-b border-gray-100 z-10 shadow-sm">
        <div className="flex items-center justify-between mb-5 py-3">
          <button
            onClick={() => navigate(-1)}
            className="p-1 active:scale-90 transition-transform"
          >
            <ArrowLeft className="w-6 h-6 text-gray-800" />
          </button>
          <h1 className="text-2xl font-bold text-gray-800">운동 통계</h1>
          <div className="w-10"></div>
        </div>

        {/* CLEAN FILTERS */}
        <div className="space-y-4">
          {/* 기간 & 운동종류 - 깨끗한 화이트 스타일 */}
          <div className="grid grid-cols-2 gap-3">
            <div className="relative">
              <select
                value={period}
                onChange={(e) => {
                  setPeriod(e.target.value);
                  setSelectedWeek(null);
                  setSelectedMonth(null);
                  setSelectedYear(null);
                }}
                className="w-full pl-4 pr-10 py-3 text-sm font-bold text-gray-700 bg-white border border-gray-200 rounded-2xl shadow-sm appearance-none focus:outline-none focus:ring-2 focus:ring-[rgb(0,224,198)] focus:border-transparent transition-all"
              >
                <option value="">전체 기간</option>
                <option value="WEEK">주간</option>
                <option value="MONTH">월간</option>
                <option value="YEAR">연간</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>

            <div className="relative">
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="w-full pl-4 pr-10 py-3 text-sm font-bold text-gray-700 bg-white border border-gray-200 rounded-2xl shadow-sm appearance-none focus:outline-none focus:ring-2 focus:ring-[rgb(0,224,198)] focus:border-transparent transition-all"
              >
                <option value="">운동 종류</option>
                <option value="러닝">러닝</option>
                <option value="산책">산책</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* 상세 기간 (등장 시 화이트 테두리) */}
          {period && (
            <div className="relative animate-in slide-in-from-top-1 duration-200">
              <select
                value={
                  period === "WEEK"
                    ? selectedWeek || ""
                    : period === "MONTH"
                    ? selectedMonth || ""
                    : selectedYear || ""
                }
                onChange={(e) => {
                  if (period === "WEEK") setSelectedWeek(e.target.value);
                  else if (period === "MONTH") setSelectedMonth(e.target.value);
                  else setSelectedYear(e.target.value);
                }}
                className="w-full pl-10 pr-10 py-3 text-sm font-black text-[rgb(0,224,198)] bg-white border-2 border-teal-50 rounded-2xl shadow-sm appearance-none"
              >
                <option value="">활동 시점 선택</option>
                {(period === "WEEK"
                  ? weeks
                  : period === "MONTH"
                  ? months
                  : years
                ).map((item) => (
                  <option key={item.key} value={item.key}>
                    {item.label}
                  </option>
                ))}
              </select>
              <Calendar className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[rgb(0,224,198)]" />
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[rgb(0,224,198)]" />
            </div>
          )}

          {/* 개인/대결 - 더 깔끔한 세그먼트 */}
          <div className="flex bg-gray-200/20 p-1 rounded-2xl border border-gray-100 shadow-inner">
            {[
              { label: "전체", val: "" },
              { label: "개인", val: "개인" },
              { label: "대결", val: "대결" },
            ].map((b) => (
              <button
                key={b.val}
                onClick={() => setBattleType(b.val)}
                className={`flex-1 py-2.5 text-sm font-black rounded-[14px] transition-all ${
                  battleType === b.val
                    ? "bg-white text-[rgb(0,224,198)] shadow-md translate-y-[-1px]"
                    : "text-gray-400 hover:text-gray-600"
                }`}
              >
                {b.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* MAIN CONTENT */}
      <main className="flex-1 overflow-y-auto px-5 py-6">
        {!stats ? (
          <div className="h-full flex flex-col items-center justify-center text-gray-300 py-20">
            <Activity className="w-16 h-16 mb-4 opacity-10" />
            <p className="font-bold text-lg">해당 기간의 데이터가 없어요</p>
          </div>
        ) : (
          <div className="space-y-6 pb-24 max-w-md mx-auto">
            {/* 메인 총 거리 */}
            <div className="bg-white rounded-[32px] p-7 shadow-sm border border-gray-100 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">
                  Cumulative Distance
                </p>
                <h2 className="text-3xl font-black text-gray-900 leading-none">
                  {formatValue(stats.sum_distance_all)}{" "}
                  <span className="text-lg font-bold text-gray-300">km</span>
                </h2>
              </div>
              <div className="w-14 h-14 bg-teal-50 rounded-2xl flex items-center justify-center">
                <Route className="w-8 h-8 text-[rgb(0,224,198)]" />
              </div>
            </div>

            {/* 추이 그래프 */}
            <div className="bg-white rounded-[32px] p-6 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 mb-6 px-1">
                <TrendingUp className="w-5 h-5 text-[rgb(0,224,198)]" />
                <h3 className="font-black text-gray-800">활동 리듬</h3>
              </div>

              <div className="w-full">
                <ResponsiveContainer width="100%" height={180}>
                  <AreaChart data={chartData} margin={{ bottom: 0 }}>
                    <defs>
                      <linearGradient
                        id="colorDist"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor={mainTeal}
                          stopOpacity={0.2}
                        />
                        <stop
                          offset="95%"
                          stopColor={mainTeal}
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>

                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#f0f0f0"
                    />

                    <XAxis
                      dataKey="name"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fontWeight: 700, fill: "#adb5bd" }}
                      dy={8}
                    />

                    {/* domain의 시작점을 -0.5 정도로 주면 0.2인 선이 바닥에서 살짝 떠 보입니다 */}
                    <YAxis
                      domain={[
                        isAllZero ? -0.5 : 0,
                        Math.max(5, maxDistance * 1.2),
                      ]}
                      hide
                    />

                    <Tooltip
                      active={isAllZero ? false : undefined}
                      contentStyle={{
                        borderRadius: "16px",
                        border: "none",
                        boxShadow: "0 8px 16px rgba(0,0,0,0.08)",
                      }}
                    />

                    {/* 1. 데이터가 없을 때: 회색 실선 (기존 로직 영향 X) */}
                    {isAllZero && (
                      <Area
                        type="monotone"
                        dataKey="distance"
                        stroke="#cbd5e1" // 회색 실선 (gray-300)
                        strokeWidth={2}
                        fill="none"
                        isAnimationActive={false}
                      />
                    )}

                    {/* 빈 차트일 때 → 회색 실선 */}
                    {isAllZero && (
                      <Area
                        type="monotone"
                        dataKey="distance"
                        stroke="#cbd5e1" // ✅ 회색
                        strokeWidth={2}
                        fill="none"
                        isAnimationActive={false}
                      />
                    )}

                    {/* 데이터 있을 때 → 기존 디자인 유지 */}
                    {!isAllZero && (
                      <Area
                        type="monotone"
                        dataKey="distance"
                        stroke={mainTeal} // ✅ 기존 민트색
                        strokeWidth={4}
                        fill="url(#colorDist)"
                      />
                    )}
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* 통계 상세 그리드 */}
            <div className="grid grid-cols-2 gap-4">
              {[
                {
                  label: "평균 속도",
                  value: stats.avg_speed_all,
                  unit: "km/h",
                  icon: <Gauge />,
                  color: "text-blue-500",
                  bg: "bg-blue-50",
                },
                {
                  label: "평균 케이던스",
                  value: stats.avg_cadence_all,
                  unit: "spm",
                  icon: <Activity />,
                  color: "text-indigo-500",
                  bg: "bg-indigo-50",
                },
                {
                  label: "칼로리 소모",
                  value: stats.sum_calorie_all,
                  unit: "kcal",
                  icon: <Flame />,
                  color: "text-orange-500",
                  bg: "bg-orange-50",
                },
                {
                  label: "운동 횟수",
                  value: stats.workrecord_count,
                  unit: "회",
                  icon: <Layers />,
                  color: "text-purple-500",
                  bg: "bg-purple-50",
                },
                {
                  label: "평균 페이스",
                  value: stats.avg_pace_all,
                  unit: "분/km",
                  icon: <Timer />,
                  color: "text-green-500",
                  bg: "bg-green-50",
                },
                {
                  label: "평균 심박수",
                  value: stats.avg_heartrate_all,
                  unit: "bpm",
                  icon: <HeartPulse />,
                  color: "text-red-500",
                  bg: "bg-red-50",
                },
              ].map((item, idx) => (
                <WorkRecordStatCard key={idx} {...item} />
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default StatsWorkRecordPage;
